import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import styles from './PublicEntryPage.module.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Math.abs(value ?? 0));
}

function formatDate(isoDate) {
  if (!isoDate) return null;
  const [y, m, d] = isoDate.split('-');
  return `${d}/${m}/${y}`;
}

const PublicEntryPage = () => {
  const { slug } = useParams();
  const [status, setStatus] = useState('loading'); // loading | pin_required | loaded | expired | not_found | error
  const [data, setData] = useState(null);
  const [pin, setPin] = useState(['', '', '', '']);
  const [pinError, setPinError] = useState('');
  const [pinLoading, setPinLoading] = useState(false);
  const pinRefs = [useRef(), useRef(), useRef(), useRef()];

  const fetchShare = async (pinValue) => {
    const url = pinValue
      ? `${API_URL}/api/entry-share/${slug}?pin=${pinValue}`
      : `${API_URL}/api/entry-share/${slug}`;

    const res = await fetch(url);

    if (res.status === 404) { setStatus('not_found'); return; }
    if (res.status === 410) { setStatus('expired'); return; }
    if (res.status === 401) { setStatus('pin_required'); return; }
    if (!res.ok) { setStatus('error'); return; }

    const json = await res.json();
    setData(json);
    setStatus('loaded');
  };

  useEffect(() => {
    window.history.scrollRestoration = 'manual';
    window.scrollTo({ top: 0, behavior: 'instant' });
    fetchShare(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const handlePinDigit = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...pin];
    next[index] = value.slice(-1);
    setPin(next);
    if (value && index < 3) {
      pinRefs[index + 1].current?.focus();
    } else if (value && index === 3) {
      const pinValue = [...next].join('');
      if (pinValue.length === 4) {
        setPinLoading(true);
        setPinError('');
        fetch(`${API_URL}/api/entry-share/${slug}?pin=${pinValue}`)
          .then(res => {
            if (res.status === 401) {
              setPinError('Senha incorreta. Tente novamente.');
              setPin(['', '', '', '']);
              setTimeout(() => pinRefs[0].current?.focus(), 50);
              setPinLoading(false);
              return null;
            }
            if (res.status === 410) { setStatus('expired'); return null; }
            if (!res.ok) { setStatus('error'); return null; }
            return res.json();
          })
          .then(json => {
            if (json) { setData(json); setStatus('loaded'); setPinLoading(false); }
          });
      }
    }
  };

  const handlePinKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      pinRefs[index - 1].current?.focus();
    }
  };

  const handlePinSubmit = async () => {
    const pinValue = pin.join('');
    if (pinValue.length < 4) { setPinError('Digite os 4 dígitos do PIN.'); return; }
    setPinLoading(true);
    setPinError('');
    const url = `${API_URL}/api/entry-share/${slug}?pin=${pinValue}`;
    const res = await fetch(url);
    if (res.status === 401) {
      setPinError('Senha incorreta. Tente novamente.');
      setPin(['', '', '', '']);
      setTimeout(() => pinRefs[0].current?.focus(), 50);
      setPinLoading(false);
      return;
    }
    if (res.status === 410) { setStatus('expired'); return; }
    if (!res.ok) { setStatus('error'); return; }
    const json = await res.json();
    setData(json);
    setStatus('loaded');
    setPinLoading(false);
  };

  const handlePinKeyPress = (e) => {
    if (e.key === 'Enter') handlePinSubmit();
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.brand}>
          <img src="/logo.png" alt="Extrato logo" className={styles.brandLogo} />
          <span>Extrato</span>
        </div>

        {status === 'loading' && (
          <div className={styles.statusCard}>
            <div className={styles.statusIcon}>⏳</div>
            <p className={styles.statusTitle}>Carregando...</p>
          </div>
        )}

        {status === 'not_found' && (
          <div className={styles.statusCard}>
            <div className={styles.statusIcon}>🔍</div>
            <p className={styles.statusTitle}>Comprovante não encontrado.</p>
          </div>
        )}

        {status === 'expired' && (
          <div className={styles.statusCard}>
            <div className={styles.statusIcon}>⏰</div>
            <p className={styles.statusTitle}>Este link expirou.</p>
          </div>
        )}

        {status === 'error' && (
          <div className={styles.statusCard}>
            <div className={styles.statusIcon}>⚠️</div>
            <p className={styles.statusTitle}>Erro ao carregar o comprovante.</p>
          </div>
        )}

        {status === 'pin_required' && (
          <div className={styles.pinCard}>
            <div className={styles.lockIcon}>🔒</div>
            <h2 className={styles.pinTitle}>Acesso protegido</h2>
            <p className={styles.pinSubtitle}>
              Digite o PIN de 4 dígitos para visualizar este comprovante.
            </p>
            <div className={styles.pinInputRow}>
              {pin.map((digit, i) => (
                <input
                  key={i}
                  ref={pinRefs[i]}
                  type="number"
                  inputMode="numeric"
                  className={styles.pinDigit}
                  value={digit}
                  onChange={e => handlePinDigit(i, e.target.value)}
                  onKeyDown={e => handlePinKeyDown(i, e)}
                  onKeyPress={handlePinKeyPress}
                  maxLength={1}
                  autoFocus={false}
                />
              ))}
            </div>
            {pinError && <p className={styles.pinError}>{pinError}</p>}
            <button
              className={styles.btnConfirm}
              onClick={handlePinSubmit}
              disabled={pinLoading || pin.join('').length < 4}
            >
              {pinLoading ? 'Verificando...' : 'Confirmar'}
            </button>
          </div>
        )}

        {status === 'loaded' && data && (
          <>
            <p className={styles.noteTitle} style={{ textAlign: 'center', marginBottom: 16 }}>
              {data.noteTitle}
            </p>

            {(data.entries || []).map((entry, idx) => (
              <div key={entry.entryId || idx} className={styles.card}>
                <h2 className={styles.entryLabel}>{entry.label || '—'}</h2>

                <div className={entry.value < 0 ? styles.valueExpense : styles.valueIncome}>
                  {entry.value < 0 ? '−' : '+'} {formatCurrency(entry.value)}
                </div>

                {entry.dueDate && (
                  <div className={styles.metaRow}>
                    <span>📅</span>
                    <span>Vencimento: <strong>{formatDate(entry.dueDate)}</strong></span>
                  </div>
                )}

                <div className={styles.metaRow}>
                  <span>Status:</span>
                  <span className={`${styles.badge} ${entry.isPaid ? styles.badgePaid : styles.badgePending}`}>
                    {entry.isPaid ? '✅ Pago' : '🕒 Pendente'}
                  </span>
                </div>

                {entry.attachments && entry.attachments.length > 0 && (
                  <>
                    <hr className={styles.divider} />
                    <p className={styles.sectionTitle}>Anexos</p>
                    {entry.attachments.map((att, i) => (
                      <a
                        key={att.s3Key || i}
                        href={att.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.attachmentLink}
                        download={att.fileName}
                      >
                        <span className={styles.attachmentIcon}>
                          <i className="fa-solid fa-paperclip"></i>
                        </span>
                        <span className={styles.attachmentName}>{att.fileName}</span>
                        <span className={styles.downloadIcon}>
                          <i className="fa-solid fa-arrow-down"></i>
                        </span>
                      </a>
                    ))}
                  </>
                )}
              </div>
            ))}

            <p className={styles.footer}>Compartilhado via Extrato</p>
          </>
        )}
      </div>
    </div>
  );
};

export default PublicEntryPage;
